import DoneIcon from "@mui/icons-material/Done";
import ErrorIcon from "@mui/icons-material/Error";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Backdrop,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import { Box } from "@mui/system";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Navigate, useLocation } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import * as Yup from "yup";
import Error from "../../../../components/error/error";
import { cfpBaseUrl } from "../../../../network/urls";
import "./CreatePermission.scss";
import { AllPermissions } from "../../../../network/APIs/APIs";

const initialValues = {
  iv_name: "",
  iv_group_name: "",
};

function TreeNode({
  item,
  data,
  selectedItemId,
  handleSelect,
  expandedNodes,
  setExpandedNodes,
  depth = 0,
}) {
  const hasChildren = data.some((child) => child.parent_idt === item.idt);
  const isExpanded = expandedNodes.includes(item.idt);
  const isSelected = selectedItemId === item.idt;

  const handleToggle = (e) => {
    e.stopPropagation();
    if (isExpanded) {
      setExpandedNodes(expandedNodes.filter((id) => id !== item.idt));
    } else {
      setExpandedNodes([...expandedNodes, item.idt]);
    }
  };

  return (
    <div className={`tree-node depth-${Math.min(depth, 4)}`}>
      <div
        className={`tree-row${isSelected ? " is-selected" : ""}`}
        onClick={() => handleSelect(item.idt)}
      >
        {hasChildren ? (
          <button
            type="button"
            className={`tree-toggle${isExpanded ? " is-open" : ""}`}
            onClick={handleToggle}
            aria-label={isExpanded ? "Collapse" : "Expand"}
          >
            {isExpanded ? (
              <ExpandMoreIcon fontSize="small" />
            ) : (
              <ChevronRightIcon fontSize="small" />
            )}
          </button>
        ) : (
          <span className="tree-toggle tree-toggle-spacer" />
        )}

        <input
          type="radio"
          name="selectedItem"
          className="tree-radio"
          checked={isSelected}
          onChange={() => handleSelect(item.idt)}
          value={item.idt}
          onClick={(e) => e.stopPropagation()}
        />
        <label className="tree-label">{item.name}</label>
      </div>

      {isExpanded && hasChildren && (
        <div className="tree-children">
          {data
            .filter((child) => child.parent_idt === item.idt)
            .map((child) => (
              <TreeNode
                key={child.idt}
                item={child}
                data={data}
                selectedItemId={selectedItemId}
                handleSelect={handleSelect}
                expandedNodes={expandedNodes}
                setExpandedNodes={setExpandedNodes}
                depth={depth + 1}
              />
            ))}
        </div>
      )}
    </div>
  );
}

function TreeViewWithRadioSingleSelect({ data, nodeValue }) {
  const [selectedItemId, setSelectedItemId] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState([]);

  const handleSelect = (itemId) => {
    setSelectedItemId(itemId);
    nodeValue(itemId);
  };

  if (!data || data.length === 0) {
    return (
      <div className="tree-wrapper tree-empty">
        <Typography variant="body2">No permissions available yet.</Typography>
      </div>
    );
  }

  return (
    <div className="tree-wrapper">
      {data
        .filter((item) => item.parent_idt === null)
        .map((item) => (
          <TreeNode
            key={item.idt}
            item={item}
            data={data}
            selectedItemId={selectedItemId}
            handleSelect={handleSelect}
            expandedNodes={expandedNodes}
            setExpandedNodes={setExpandedNodes}
          />
        ))}
    </div>
  );
}

const CreatePermission = () => {
  const [idt, setIdt] = useState();
  const [status, setStatus] = useState(1);
  const { token, userId, permissions } = useSelector((state) => state.auth);
  const [error, setError] = useState(false);
  const [sortedPermissions, setSortedPermissions] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [viewBackdrop, setViewBackdrop] = React.useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [errorDialogMessage, setErrorDialogMessage] = useState("");

  const errorCloses = () => {
    setError("");
  };

  const showApiErrorInDialog = (msg) => {
    const text = msg ? String(msg) : "Something went wrong.";
    setError("");
    setErrorDialogMessage(text);
    setErrorDialogOpen(true);
  };

  const nodeValue = (value) => {
    setIdt(value);
  };

  const location = useLocation();

  const refreshPermissions = async () => {
    setViewBackdrop(true);
    try {
      const { allPermissions, sortedAllPermissions } = await AllPermissions(
        token,
        permissions,
      );
      setSortedPermissions(sortedAllPermissions);
      setAllPermissions(allPermissions);
    } catch (e) {
      console.error("Failed to refresh permissions", e);
    } finally {
      setViewBackdrop(false);
    }
  };

  useEffect(() => {
    refreshPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load once on mount; use refreshPermissions on dialog close
  }, []);

  const handleSuccessDialogClose = () => {
    setSuccessDialogOpen(false);
    refreshPermissions();
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    refreshPermissions();
  };

  const { values, errors, touched, handleChange, handleBlur, handleSubmit } =
    useFormik({
      initialValues,
      validationSchema: Yup.object({
        iv_name: Yup.string().required("Enter name for permission."),
        iv_group_name: Yup.string().required(
          "Enter group name for permission."
        ),
      }),
      onSubmit: async (values, { resetForm }) => {

        var formdata = new FormData();
        if (idt !== undefined) {
          formdata.append("idt", idt);
        }
        formdata.append("name", values.iv_name);
        formdata.append("groupName", values.iv_group_name);
        formdata.append("status", status);
        formdata.append("createdBy", userId);

        const myHeaders = new Headers();
        myHeaders.append('Authorization', token);

        var requestOptions = {
          method: "POST",
          body: formdata,
          headers: myHeaders,
          redirect: "follow",
        };

        try {
          const response = await fetch(
            cfpBaseUrl + "role/addPrem",
            requestOptions,
          );
          const result = await response.json();
          const ok =
            response.ok &&
            (result.status === "200" || String(result.status) === "200");
          if (ok) {
            resetForm();
            setSuccessMessage(
              result.message || "Permission added successfully.",
            );
            setSuccessDialogOpen(true);
          } else {
            showApiErrorInDialog(result.message || "Something went wrong.");
          }
        } catch (error) {
          showApiErrorInDialog(error?.message || error);
        }
      },
    });

  return (
    <>
      <Grid container className="permissionCreation">
        <Box className="permissionCreationContainer">
          <h3 className="heading">Add Permission</h3>

          <form className="ncform" onSubmit={(e) => e.preventDefault()}>
            <div className="section-card section-card--accent">
              <div className="section-title">Basic Information</div>

              <Grid container>
                <Grid item xs={12} sm={6} md={4}>
                  <div className="field-wrap">
                    <label className="fieldLabel" htmlFor="iv_name">Name</label>
                    <TextField
                      id="iv_name"
                      type="text"
                      autoComplete="off"
                      onChange={handleChange}
                      name="iv_name"
                      value={values.iv_name}
                      onBlur={handleBlur}
                      className="textInputField"
                      placeholder="Enter permission name"
                      fullWidth
                    />
                    <Box className="from-Error">
                      {errors.iv_name && touched.iv_name ? errors.iv_name : null}
                    </Box>
                  </div>
                </Grid>

                <Grid item xs={12} sm={6} md={4}>
                  <div className="field-wrap">
                    <label className="fieldLabel" htmlFor="iv_group_name">Group</label>
                    <TextField
                      id="iv_group_name"
                      type="text"
                      autoComplete="off"
                      onChange={handleChange}
                      name="iv_group_name"
                      value={values.iv_group_name}
                      onBlur={handleBlur}
                      className="textInputField"
                      placeholder="Enter group name"
                      fullWidth
                    />
                    <Box className="from-Error">
                      {errors.iv_group_name && touched.iv_group_name
                        ? errors.iv_group_name
                        : null}
                    </Box>
                  </div>
                </Grid>

                <Grid item xs={12} sm={12} md={4}>
                  <div className="field-wrap">
                    <label className="fieldLabel">Status</label>
                    <FormControl className="form-control">
                      <RadioGroup
                        className="radio-group"
                        row
                        name="iv_applicant_status"
                        defaultValue={status}
                        value={status}
                        onChange={() => {
                          if (status === 1) {
                            setStatus(0);
                          } else {
                            setStatus(1);
                          }
                        }}
                      >
                        <FormControlLabel
                          value={1}
                          className="form-control-label"
                          control={<Radio />}
                          label="Active"
                          name="Active"
                          labelPlacement="end"
                        />
                        <FormControlLabel
                          value={0}
                          className="form-control-label"
                          control={<Radio />}
                          label="Inactive"
                          name="Inactive"
                          labelPlacement="end"
                        />
                      </RadioGroup>
                    </FormControl>
                  </div>
                </Grid>
              </Grid>
            </div>

            <div className="section-card permissions-section">
              <div className="section-title-row">
                <div>
                  <div className="section-title">Parent Permission</div>
                  <p className="section-subtitle">
                    Select a parent node from the hierarchy to nest this new permission under, or leave unselected to add it at the top level.
                  </p>
                </div>
                <span className={`parent-pill${idt ? " has-value" : ""}`}>
                  {idt ? `Parent ID: ${idt}` : "No parent selected"}
                </span>
              </div>

              <TreeViewWithRadioSingleSelect
                data={allPermissions}
                nodeValue={nodeValue}
              />
            </div>

            {error && (
              <div className="error-wrap">
                <Error
                  error={error}
                  onClose={() => errorCloses()}
                  backgroundColor="#FFC0CB"
                  textColor="#bb0303"
                  iconColor="#bb0303"
                />
              </div>
            )}

            <Box className="submit-area">
              <Button
                variant="contained"
                size="medium"
                className="submit-btn"
                onClick={handleSubmit}
              >
                Submit
              </Button>
            </Box>
          </form>
        </Box>
      </Grid>

      {/* Backdrop */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1000 }}
        open={viewBackdrop}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {/* Success Dialog */}
      <Dialog
        open={successDialogOpen}
        onClose={handleSuccessDialogClose}
        PaperProps={{ className: "feedback-dialog feedback-dialog--success" }}
      >
        <IconButton
          aria-label="close"
          onClick={handleSuccessDialogClose}
          className="feedback-dialog__close"
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogTitle className="feedback-dialog__title">
          <span className="feedback-dialog__icon feedback-dialog__icon--success">
            <DoneIcon />
          </span>
          <Typography component="div" className="feedback-dialog__heading">
            Success
          </Typography>
        </DialogTitle>
        <DialogContent className="feedback-dialog__content">
          <DialogContentText className="feedback-dialog__text">
            {successMessage || "Permission added successfully."}
          </DialogContentText>
        </DialogContent>
      </Dialog>

      {/* Error Dialog */}
      <Dialog
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        PaperProps={{ className: "feedback-dialog feedback-dialog--error" }}
      >
        <IconButton
          aria-label="close"
          onClick={handleErrorDialogClose}
          className="feedback-dialog__close"
          size="small"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <DialogTitle className="feedback-dialog__title">
          <span className="feedback-dialog__icon feedback-dialog__icon--error">
            <ErrorIcon />
          </span>
          <Typography component="div" className="feedback-dialog__heading">
            Something went wrong
          </Typography>
        </DialogTitle>
        <DialogContent className="feedback-dialog__content">
          <DialogContentText className="feedback-dialog__text">
            {errorDialogMessage || "Something went wrong."}
          </DialogContentText>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CreatePermission;
